import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from 'next-themes';
import { 
  Fuel, 
  Check, 
  ArrowRight, 
  Play,
  Store,
  Smartphone,
  BarChart3,
  Shield,
  Clock,
  DollarSign,
  Users,
  Zap,
  Cloud,
  Headphones,
  ChevronRight,
  Star,
  Quote,
  Monitor,
  Gauge,
  Droplets,
  CreditCard,
  Bell,
  TrendingUp,
  Package,
  Truck,
  Award,
  CheckCircle2,
  X,
  AlertTriangle,
  Frown,
  Smile,
  Timer,
  Wifi,
  WifiOff,
  Calculator,
  ArrowDown,
  Sparkles,
  MousePointer,
  Eye,
  Brain,
  Bot,
  LineChart,
  Sun,
  Moon,
  Cpu,
  Nfc,
  Banknote,
  QrCode,
  Fingerprint,
  Lock,
  MapPin,
  ArrowDownUp,
  Building,
  Wallet,
  PiggyBank,
  Handshake,
  FileText,
  BadgePercent
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { FlowFeedLogo } from '../components/Logo';
import { Helmet } from 'react-helmet-async';

const GasStationLanding = () => {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [roiInputs, setRoiInputs] = useState({
    pumps: 8,
    dailyGallons: 3000,
    currentSoftwareCost: 400
  });

  // Hero carousel images
  const heroSlides = [
    {
      image: "https://images.unsplash.com/photo-1759270977890-d448ea33862b?w=800&h=600&fit=crop&q=80",
      title: "Gas Station Overview",
      subtitle: "FlowFeed Gas Station POS",
      badge: "AI-Powered",
      stats: { pumps: 8, sales: "$2.4K", tank: "94%" }
    },
    {
      image: "https://images.unsplash.com/photo-1765648188992-b50cd64df193?w=800&h=600&fit=crop&q=80",
      title: "Modern Fuel Station",
      subtitle: "Multi-Pump Management",
      badge: "24/7 Operations",
      stats: { pumps: 12, sales: "$4.2K", tank: "87%" }
    },
    {
      image: "https://images.unsplash.com/photo-1636249591075-b32a50249ad2?w=800&h=600&fit=crop&q=80",
      title: "Tuck Shop & C-Store",
      subtitle: "Integrated Retail",
      badge: "Fast Checkout",
      stats: { pumps: 8, sales: "$1.8K", tank: "92%" }
    },
    {
      image: "https://images.unsplash.com/photo-1762680585321-4b7a07dbad33?w=800&h=600&fit=crop&q=80",
      title: "Cash Register & Payments",
      subtitle: "AI Cash Register",
      badge: "US Standard",
      stats: { pumps: 8, sales: "$2.9K", tank: "89%" }
    }
  ];

  // Auto-rotate carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000); // Change slide every 5 seconds
    return () => clearInterval(interval);
  }, [heroSlides.length]);

  // Ensure theme is mounted before rendering toggle
  useEffect(() => {
    setMounted(true);
  }, []);
  
  // Calculate ROI
  const calculateROI = () => {
    const monthlyFlowFeedCost = 299;
    const savingsFromEfficiency = roiInputs.dailyGallons * 0.02 * 30; // 2 cents per gallon efficiency
    const savingsFromSoftware = roiInputs.currentSoftwareCost - monthlyFlowFeedCost;
    const totalMonthlySavings = savingsFromEfficiency + Math.max(0, savingsFromSoftware);
    const annualSavings = totalMonthlySavings * 12;
    return { monthly: totalMonthlySavings, annual: annualSavings };
  };

  const roi = calculateROI();

  // AI-Powered Features
  const aiFeatures = [
    {
      icon: Brain,
      title: "AI Price Optimizer",
      description: "Automatically adjusts fuel prices based on competitor data, demand patterns, and profit targets."
    },
    {
      icon: LineChart,
      title: "Predictive Analytics",
      description: "Forecasts fuel demand, identifies peak hours, and suggests optimal staffing levels."
    },
    {
      icon: Bot,
      title: "Smart Inventory",
      description: "AI monitors C-store inventory and auto-generates restock orders before you run out."
    },
    {
      icon: Cpu,
      title: "Fraud Detection",
      description: "Machine learning identifies suspicious transactions and drive-offs in real-time."
    },
    {
      icon: Calculator,
      title: "AI Cash Register",
      description: "Smart checkout that learns your business patterns and speeds up transactions."
    },
  ];

  const features = [
    {
      icon: Fuel,
      title: "8-Pump Control Panel",
      description: "See all pumps at a glance. Authorize prepay, monitor pumping, collect payments - all from one screen.",
      color: "from-orange-500 to-amber-500"
    },
    {
      icon: Calculator,
      title: "AI Cash Register",
      description: "Smart checkout with auto-suggestions, quick keys, and multi-payment support. The fastest register in the business.",
      color: "from-cyan-500 to-blue-500"
    },
    {
      icon: Store,
      title: "Integrated C-Store POS",
      description: "Fuel and snacks in one transaction. No switching between systems. Your cashiers will love it.",
      color: "from-emerald-500 to-teal-500"
    },
    {
      icon: Gauge,
      title: "Real-Time Tank Levels",
      description: "Know exactly how much fuel you have. Get alerts before you run low. Never lose a sale.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: Shield,
      title: "21+ Age Verification",
      description: "Built-in compliance for tobacco and alcohol. Digital logs protect you from fines.",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: Brain,
      title: "AI-Powered Pricing",
      description: "Get smart suggestions based on competitor prices, demand, and margins. Maximize every gallon.",
      color: "from-indigo-500 to-purple-500"
    },
    {
      icon: Smartphone,
      title: "Mobile Dashboard",
      description: "Check sales, tank levels, and alerts from your phone. Manage your station from anywhere.",
      color: "from-sky-500 to-blue-500"
    },
  ];

  const painPoints = [
    {
      old: "Clunky DOS-like interface from the 90s",
      new: "Modern touch-screen interface your staff learns in 30 minutes",
      icon: Monitor
    },
    {
      old: "Separate systems for pumps and store - double entry headaches",
      new: "One unified system - fuel + c-store in single transaction",
      icon: Package
    },
    {
      old: "Can only check sales when you're physically at the station",
      new: "Real-time mobile app - monitor from home, vacation, anywhere",
      icon: Smartphone
    },
    {
      old: "Expensive on-site service calls for every issue ($150+/visit)",
      new: "24/7 remote support included - most issues fixed in minutes",
      icon: Headphones
    },
    {
      old: "Manual tank readings - drive to station just to check levels",
      new: "Automatic tank monitoring with low-level SMS alerts",
      icon: Gauge
    },
    {
      old: "Paper compliance logs - nightmare during audits",
      new: "Digital age verification with instant audit reports",
      icon: Shield
    },
  ];

  const comparison = [
    { feature: "Touch Screen Interface", flowfeed: true, traditional: false },
    { feature: "Mobile App Access", flowfeed: true, traditional: false },
    { feature: "AI Analytics & Insights", flowfeed: true, traditional: false },
    { feature: "Real-time SMS/Email Alerts", flowfeed: true, traditional: false },
    { feature: "Cloud Backup & Security", flowfeed: true, traditional: false },
    { feature: "Multi-Location Dashboard", flowfeed: true, traditional: false },
    { feature: "Integrated Loyalty Program", flowfeed: "Included", traditional: "+$50/mo" },
    { feature: "24/7 Phone Support", flowfeed: "Included", traditional: "+$75/mo" },
    { feature: "Remote Software Updates", flowfeed: "Automatic", traditional: "$200/visit" },
    { feature: "Typical Monthly Cost", flowfeed: "$299", traditional: "$450+" },
  ];

  const testimonials = [
    {
      quote: "Switched from Gilbarco's old system. My employees figured it out in one shift. Wish I'd done this years ago.",
      author: "Mike Thompson",
      role: "Owner, Quick Stop Gas",
      location: "Austin, TX",
      savings: "$180/month saved"
    },
    {
      quote: "The AI pricing told me I was 3 cents below the Chevron down the street. Fixed it and made an extra $400 that week.",
      author: "Sarah Chen",
      role: "Manager, Chen's Gas & Go",
      location: "Houston, TX",
      savings: "+$1,600/month revenue"
    },
    {
      quote: "Got a tank low alert at 2am. Scheduled delivery before opening. Old system? Would've run dry by noon.",
      author: "David Rodriguez",
      role: "Owner, 3 Stations",
      location: "Phoenix, AZ",
      savings: "Prevented $5K loss"
    },
  ];

  const pricingPlans = [
    {
      name: "Starter",
      price: 149,
      description: "Small stations",
      features: [
        "Up to 4 pumps",
        "Basic tank monitoring",
        "Email support",
        "Standard reports",
      ],
      notIncluded: ["C-Store POS", "Mobile App", "AI Analytics"],
      popular: false
    },
    {
      name: "Professional",
      price: 299,
      description: "Most popular",
      features: [
        "Up to 16 pumps",
        "Full C-Store POS",
        "Mobile app access",
        "AI-powered analytics",
        "24/7 phone support",
        "Loyalty program",
        "SMS/Email alerts",
      ],
      notIncluded: [],
      popular: true
    },
    {
      name: "Enterprise",
      price: 499,
      description: "Multi-location",
      features: [
        "Unlimited pumps",
        "Multi-site dashboard",
        "Dedicated account manager",
        "Custom integrations",
        "On-site training",
        "Priority support",
        "API access",
      ],
      notIncluded: [],
      popular: false
    },
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${theme === 'dark' ? 'bg-slate-950 text-white' : 'bg-white text-slate-900'}`}>
      {/* SEO Title */}
      <Helmet>
        <title>Gas Station POS & Smart Station Management | FlowFeed POS</title>
      </Helmet>
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b transition-colors ${theme === 'dark' ? 'bg-slate-950/90 border-slate-800' : 'bg-white/90 border-slate-200'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <FlowFeedLogo size="small" showTagline={false} />
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${theme === 'dark' ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-600'}`}>
              AI Station POS
            </span>
          </Link>
          <div className={`hidden md:flex items-center gap-6 text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
            <a href="#ai-features" className={`hover:${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>AI Features</a>
            <a href="#payment-integration" className={`hover:${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Payments</a>
            <a href="#features" className={`hover:${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Features</a>
            <a href="#demo" className={`hover:${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Live Demo</a>
            <a href="#comparison" className={`hover:${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Why Switch</a>
            <a href="#pricing" className={`hover:${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Pricing</a>
          </div>
          <div className="flex items-center gap-3">
            {/* Theme Toggle */}
            {mounted && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                className={`rounded-full ${theme === 'dark' ? 'text-slate-400 hover:text-white hover:bg-slate-800' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`}
                data-testid="theme-toggle"
              >
                {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>
            )}
            <Link to="/pos/gas-station-demo">
              <Button variant="outline" size="sm" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                <Play className="h-4 w-4 mr-1" /> Demo
              </Button>
            </Link>
            <Link to="/signup">
              <Button size="sm" className="bg-gradient-to-r from-orange-500 to-amber-500 text-white">
                Start Free Trial
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section with Image */}
      <section className="pt-24 pb-16 px-6 relative overflow-hidden">
        {/* Background gradients */}
        <div className={`absolute inset-0 ${theme === 'dark' ? 'bg-[radial-gradient(circle_at_30%_20%,_rgba(249,115,22,0.15),_transparent_50%)]' : 'bg-[radial-gradient(circle_at_30%_20%,_rgba(249,115,22,0.08),_transparent_50%)]'}`}></div>
        <div className={`absolute inset-0 ${theme === 'dark' ? 'bg-[radial-gradient(circle_at_70%_80%,_rgba(245,158,11,0.1),_transparent_50%)]' : 'bg-[radial-gradient(circle_at_70%_80%,_rgba(245,158,11,0.05),_transparent_50%)]'}`}></div>
        
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text Content */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500/20 to-amber-500/20 border border-orange-500/30 rounded-full px-4 py-2 mb-6">
                <Brain className="h-4 w-4 text-orange-500" />
                <span className={`text-sm font-medium ${theme === 'dark' ? 'text-orange-300' : 'text-orange-600'}`}>AI-Powered Gas Station POS</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 leading-tight">
                <span className={`line-through ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>Old Pump Software</span>
                <br />
                <span className="bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">
                  Smart Station Management
                </span>
              </h1>
              
              <p className={`text-lg sm:text-xl mb-8 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                FlowFeed uses <span className="font-bold text-orange-500">AI</span> to optimize pricing, predict demand, 
                and automate your gas station. Pumps + C-Store in one beautiful system.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-orange-500 to-amber-500 hover:opacity-90 text-lg px-8 text-white"
                  onClick={() => navigate('/pos/gas-station-demo')}
                  data-testid="hero-demo-btn"
                >
                  <Play className="h-5 w-5 mr-2" /> Try Interactive Demo
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className={`text-lg px-8 ${theme === 'dark' ? 'border-slate-600' : 'border-slate-300'}`}
                  onClick={() => document.getElementById('comparison').scrollIntoView({ behavior: 'smooth' })}
                >
                  See Comparison
                </Button>
              </div>

              <div className={`flex flex-wrap items-center gap-6 text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  <span>No credit card</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  <span>7-day free trial</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  <span>AI included</span>
                </div>
              </div>
            </motion.div>

            {/* Right: Hero Image Carousel */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
            >
              <div className={`relative rounded-2xl overflow-hidden shadow-2xl border ${theme === 'dark' ? 'border-slate-700' : 'border-slate-200'}`}>
                {/* Carousel Container */}
                <div className="relative">
                  {heroSlides.map((slide, index) => (
                    <div
                      key={index}
                      className={`transition-opacity duration-700 ${index === currentSlide ? 'opacity-100' : 'opacity-0 absolute inset-0'}`}
                    >
                      {/* Slide Image */}
                      <img 
                        src={slide.image}
                        alt={slide.title}
                        className="w-full h-[400px] object-cover"
                      />
                      
                      {/* FlowFeed Branding Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                      
                      {/* Branding Badge */}
                      <div className="absolute top-4 left-4">
                        <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg px-3 py-2 flex items-center gap-2 shadow-lg">
                          <div className="h-8 w-8 bg-white rounded flex items-center justify-center">
                            <Fuel className="h-5 w-5 text-orange-500" />
                          </div>
                          <div>
                            <p className="text-white font-bold text-sm">FlowFeed</p>
                            <p className="text-orange-100 text-xs">{slide.subtitle}</p>
                          </div>
                        </div>
                      </div>

                      {/* Dynamic Badge */}
                      <div className="absolute top-4 right-4">
                        <div className="bg-purple-600/90 backdrop-blur rounded-full px-3 py-1.5 flex items-center gap-2">
                          <Brain className="h-4 w-4 text-white" />
                          <span className="text-white text-xs font-bold">{slide.badge}</span>
                        </div>
                      </div>

                      {/* Slide Title */}
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
                        <p className="text-white/90 text-2xl font-bold drop-shadow-lg">{slide.title}</p>
                      </div>

                      {/* Bottom Stats Overlay */}
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <div className="grid grid-cols-3 gap-3">
                          <div className="bg-white/10 backdrop-blur rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-white">{slide.stats.pumps}</p>
                            <p className="text-xs text-white/80">Pumps</p>
                          </div>
                          <div className="bg-emerald-500/20 backdrop-blur rounded-lg p-3 text-center border border-emerald-500/30">
                            <p className="text-2xl font-bold text-emerald-400">{slide.stats.sales}</p>
                            <p className="text-xs text-emerald-300">Today's Sales</p>
                          </div>
                          <div className="bg-white/10 backdrop-blur rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-white">{slide.stats.tank}</p>
                            <p className="text-xs text-white/80">Tank Level</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Carousel Indicators */}
                <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                  {heroSlides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-2.5 h-2.5 rounded-full transition-all ${
                        index === currentSlide 
                          ? 'bg-orange-500 w-8' 
                          : 'bg-white/50 hover:bg-white/80'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>

                {/* Navigation Arrows */}
                <button
                  onClick={() => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center text-white transition-colors z-10"
                  aria-label="Previous slide"
                >
                  <ChevronRight className="h-6 w-6 rotate-180" />
                </button>
                <button
                  onClick={() => setCurrentSlide((prev) => (prev + 1) % heroSlides.length)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center text-white transition-colors z-10"
                  aria-label="Next slide"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </div>

              {/* Floating AI Insight Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className={`absolute -bottom-6 -left-6 ${theme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'} border rounded-xl p-4 shadow-xl max-w-[220px]`}
              >
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                    <Brain className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className={`text-xs font-medium ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>AI Insight</p>
                    <p className={`text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Raise price by 3¢</p>
                    <p className="text-xs text-emerald-500">+$180/day revenue</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* AI Features Section */}
      <section id="ai-features" className={`py-20 px-6 ${theme === 'dark' ? 'bg-gradient-to-b from-purple-950/30 to-slate-950' : 'bg-gradient-to-b from-purple-50 to-white'}`}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-purple-500/20 border border-purple-500/30 rounded-full px-4 py-2 mb-4">
              <Sparkles className="h-4 w-4 text-purple-400" />
              <span className={`text-sm font-medium ${theme === 'dark' ? 'text-purple-300' : 'text-purple-600'}`}>Powered by AI</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              AI That <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">Runs Your Station</span>
            </h2>
            <p className={`text-lg max-w-2xl mx-auto ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
              FlowFeed's AI works 24/7 to optimize pricing, predict demand, prevent fraud, and maximize your profits.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aiFeatures.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`${theme === 'dark' ? 'bg-slate-900/50 border-slate-800 hover:border-purple-500/50' : 'bg-white border-slate-200 hover:border-purple-300'} border rounded-2xl p-6 transition-colors`}
              >
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-bold mb-2">{feature.title}</h3>
                <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>{feature.description}</p>
              </motion.div>
            ))}
          </div>

          {/* AI Demo CTA */}
          <div className="mt-12 text-center">
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
              onClick={() => navigate('/pos/gas-station-demo')}
            >
              <Brain className="h-5 w-5 mr-2" /> See AI in Action
            </Button>
          </div>
        </div>
      </section>

      {/* Payment Terminal Integration Section */}
      <section id="payment-integration" className={`py-20 px-6 ${theme === 'dark' ? 'bg-slate-900/50' : 'bg-blue-50/50'}`}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-500/30 rounded-full px-4 py-2 mb-4">
              <CreditCard className="h-4 w-4 text-blue-400" />
              <span className={`text-sm font-medium ${theme === 'dark' ? 'text-blue-300' : 'text-blue-600'}`}>US Payment Standards</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Integrated <span className="bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">Payment Terminals</span>
            </h2>
            <p className={`text-lg max-w-2xl mx-auto ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
              FlowFeed connects directly with all major US payment terminals. Accept cards, contactless, and mobile payments seamlessly.
            </p>
          </div>

          {/* Payment Methods Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {[
              { icon: CreditCard, label: "Credit/Debit Cards", desc: "Visa, MC, Amex, Discover" },
              { icon: Nfc, label: "Contactless/NFC", desc: "Tap to Pay" },
              { icon: Smartphone, label: "Mobile Wallets", desc: "Apple Pay, Google Pay" },
              { icon: QrCode, label: "QR Payments", desc: "Venmo, Cash App" },
              { icon: Banknote, label: "Cash Handling", desc: "With smart drawer" },
              { icon: Shield, label: "EMV Chip", desc: "Secure chip cards" },
              { icon: Fingerprint, label: "PIN Debit", desc: "Secure PIN entry" },
              { icon: Lock, label: "PCI Compliant", desc: "End-to-end encryption" },
            ].map((method, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.05 }}
                className={`p-4 rounded-xl text-center ${theme === 'dark' ? 'bg-slate-800/50 border border-slate-700' : 'bg-white border border-slate-200 shadow-sm'}`}
              >
                <div className={`h-10 w-10 rounded-lg mx-auto mb-2 flex items-center justify-center ${theme === 'dark' ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                  <method.icon className="h-5 w-5 text-blue-500" />
                </div>
                <p className="font-bold text-sm">{method.label}</p>
                <p className={`text-xs ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>{method.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Supported Terminals */}
          <div className={`rounded-2xl p-8 ${theme === 'dark' ? 'bg-slate-800/50 border border-slate-700' : 'bg-white border border-slate-200 shadow-lg'}`}>
            <h3 className="text-xl font-bold text-center mb-6">Supported Payment Terminals</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {[
                { name: "Verifone", models: "VX, P400, e285", color: "from-blue-600 to-blue-800" },
                { name: "Ingenico", models: "Lane, Desk series", color: "from-red-500 to-red-700" },
                { name: "PAX", models: "A920, A80, S300", color: "from-green-500 to-green-700" },
                { name: "Square", models: "Terminal, Reader", color: "from-slate-600 to-slate-800" },
                { name: "Clover", models: "Flex, Mini, Station", color: "from-emerald-500 to-emerald-700" },
                { name: "Stripe", models: "BBPOS, WisePOS", color: "from-purple-500 to-indigo-600" },
              ].map((terminal, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="text-center"
                >
                  <div className={`h-16 w-16 rounded-2xl bg-gradient-to-br ${terminal.color} mx-auto mb-3 flex items-center justify-center shadow-lg`}>
                    <CreditCard className="h-8 w-8 text-white" />
                  </div>
                  <p className="font-bold">{terminal.name}</p>
                  <p className={`text-xs ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>{terminal.models}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Integration Features */}
          <div className="grid md:grid-cols-3 gap-6 mt-8">
            <div className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-slate-800/30 border border-slate-700' : 'bg-white border border-slate-200'}`}>
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-white" />
              </div>
              <h4 className="font-bold mb-2">Plug & Play Setup</h4>
              <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                Connect your terminal via USB or network. FlowFeed auto-detects and configures it in minutes.
              </p>
            </div>
            <div className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-slate-800/30 border border-slate-700' : 'bg-white border border-slate-200'}`}>
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-4">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
              <h4 className="font-bold mb-2">Real-Time Sync</h4>
              <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                Transactions sync instantly between terminal and POS. No double entry, no reconciliation headaches.
              </p>
            </div>
            <div className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-slate-800/30 border border-slate-700' : 'bg-white border border-slate-200'}`}>
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center mb-4">
                <DollarSign className="h-6 w-6 text-white" />
              </div>
              <h4 className="font-bold mb-2">Low Processing Fees</h4>
              <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                We partner with top processors to get you rates as low as 2.2% + $0.05 per transaction.
              </p>
            </div>
          </div>

          {/* Get Terminal From Us - CTA Section */}
          <div className={`mt-12 rounded-2xl p-8 ${theme === 'dark' ? 'bg-gradient-to-r from-emerald-900/50 to-cyan-900/50 border border-emerald-500/30' : 'bg-gradient-to-r from-emerald-50 to-cyan-50 border border-emerald-200'}`}>
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full px-3 py-1 mb-4">
                  <Package className="h-4 w-4 text-emerald-400" />
                  <span className="text-sm font-medium text-emerald-400">No Terminal? No Problem!</span>
                </div>
                <h3 className="text-2xl font-bold mb-3">Get a Terminal From FlowFeed</h3>
                <p className={`mb-4 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                  Don't have a payment terminal? We'll provide one! Get pre-configured hardware that works instantly with FlowFeed POS.
                </p>
                <ul className="space-y-2 mb-6">
                  {[
                    "Free terminal with 2-year contract",
                    "Pre-configured & ready to use",
                    "24/7 technical support included",
                    "Free replacement if damaged",
                    "Competitive processing rates"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-3">
                  <Button className="bg-gradient-to-r from-emerald-500 to-cyan-500 text-white">
                    <CreditCard className="h-4 w-4 mr-2" /> Request Free Terminal
                  </Button>
                  <Button variant="outline" className={theme === 'dark' ? 'border-emerald-500/50 text-emerald-400' : 'border-emerald-500 text-emerald-600'}>
                    View Pricing
                  </Button>
                </div>
              </div>
              
              {/* Terminal Options */}
              <div className="grid grid-cols-2 gap-4">
                {[
                  { name: "FlowFeed Reader", price: "FREE", desc: "Card & NFC", features: ["Tap to Pay", "Chip & Swipe", "Bluetooth"] },
                  { name: "FlowFeed Terminal", price: "$49/mo", desc: "Full Terminal", features: ["Built-in printer", "PIN pad", "Countertop"] },
                  { name: "FlowFeed Flex", price: "$79/mo", desc: "Portable", features: ["Wireless", "Long battery", "Receipt printer"] },
                  { name: "FlowFeed Station", price: "$149/mo", desc: "All-in-One", features: ["15\" touchscreen", "Cash drawer", "Complete POS"] },
                ].map((terminal, i) => (
                  <div 
                    key={i} 
                    className={`p-4 rounded-xl text-center ${theme === 'dark' ? 'bg-slate-800/80 border border-slate-700' : 'bg-white border border-slate-200 shadow-sm'}`}
                  >
                    <div className={`h-12 w-12 rounded-xl mx-auto mb-2 flex items-center justify-center ${theme === 'dark' ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
                      <CreditCard className="h-6 w-6 text-emerald-500" />
                    </div>
                    <p className="font-bold text-sm">{terminal.name}</p>
                    <p className="text-emerald-500 font-bold">{terminal.price}</p>
                    <p className={`text-xs mb-2 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>{terminal.desc}</p>
                    <div className="space-y-1">
                      {terminal.features.map((f, j) => (
                        <p key={j} className={`text-xs ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>{f}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Processing Rates */}
          <div className={`mt-8 rounded-2xl p-6 ${theme === 'dark' ? 'bg-slate-800/30 border border-slate-700' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <h3 className="text-lg font-bold text-center mb-6">FlowFeed Payment Processing Rates</h3>
            <div className="grid md:grid-cols-4 gap-4">
              {[
                { type: "In-Person", rate: "2.6% + 10¢", desc: "Tap, chip, or swipe" },
                { type: "Keyed-In", rate: "3.5% + 15¢", desc: "Manual card entry" },
                { type: "Online", rate: "2.9% + 30¢", desc: "E-commerce payments" },
                { type: "ACH/Bank", rate: "0.8%", desc: "Bank transfers (max $5)" },
              ].map((rate, i) => (
                <div key={i} className={`p-4 rounded-xl text-center ${theme === 'dark' ? 'bg-slate-900/50' : 'bg-slate-50'}`}>
                  <p className="font-bold">{rate.type}</p>
                  <p className="text-2xl font-black text-emerald-500">{rate.rate}</p>
                  <p className={`text-xs ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>{rate.desc}</p>
                </div>
              ))}
            </div>
            <p className={`text-center text-xs mt-4 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
              * No monthly fees, no hidden charges. Volume discounts available for high-transaction businesses.
            </p>
          </div>
        </div>
      </section>

      {/* Interactive Demo Preview Section */}
      <section id="demo" className={`py-16 px-6 ${theme === 'dark' ? 'bg-gradient-to-b from-slate-950 to-slate-900' : 'bg-gradient-to-b from-slate-50 to-white'}`}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">See It In Action</h2>
            <p className={`text-lg ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>Click below to launch the interactive demo - no signup required</p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="relative group cursor-pointer"
            onClick={() => navigate('/pos/gas-station-demo')}
          >
            {/* Demo Preview Card */}
            <div className={`relative border-2 rounded-2xl overflow-hidden shadow-2xl group-hover:border-orange-500/50 transition-colors ${theme === 'dark' ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-300'}`}>
              {/* Mock Demo UI */}
              <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 text-white py-2 px-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 bg-white/20 rounded-full px-3 py-1">
                    <Monitor className="h-3 w-3" />
                    <span className="text-xs font-bold">DEMO MODE</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="bg-white/20 rounded px-2 py-1 text-xs">Auto Demo</div>
                  <div className="bg-white/20 rounded px-2 py-1 text-xs">1x Speed</div>
                </div>
              </div>
              
              <div className={`grid grid-cols-3 gap-4 p-6 ${theme === 'dark' ? 'bg-slate-800' : 'bg-slate-100'}`}>
                {/* Pump Grid Preview */}
                <div className="col-span-2 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold flex items-center gap-2">
                      <Fuel className="h-4 w-4 text-orange-500" />
                      8 Fuel Pumps
                    </h3>
                    <div className="flex gap-2 text-xs">
                      <span className="bg-emerald-500/20 text-emerald-500 px-2 py-1 rounded">3 Pumping</span>
                      <span className="bg-blue-500/20 text-blue-500 px-2 py-1 rounded">1 Complete</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { status: 'pumping', amount: '$12.45' },
                      { status: 'idle', amount: '$0.00' },
                      { status: 'complete', amount: '$35.00' },
                      { status: 'pumping', amount: '$8.20' },
                      { status: 'idle', amount: '$0.00' },
                      { status: 'pumping', amount: '$22.10' },
                      { status: 'idle', amount: '$0.00' },
                      { status: 'idle', amount: '$0.00' },
                    ].map((pump, i) => (
                      <div 
                        key={i} 
                        className={`p-3 rounded-lg border text-center ${
                          pump.status === 'pumping' ? 'border-emerald-500 bg-emerald-500/10' :
                          pump.status === 'complete' ? 'border-blue-500 bg-blue-500/10' :
                          theme === 'dark' ? 'border-slate-600 bg-slate-700/50' : 'border-slate-300 bg-white'
                        }`}
                      >
                        <div className="text-xs text-slate-400 mb-1">Pump {i + 1}</div>
                        <div className="font-bold">{pump.amount}</div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* C-Store Preview */}
                <div className="space-y-3">
                  <h3 className="font-bold flex items-center gap-2">
                    <Store className="h-4 w-4 text-emerald-500" />
                    C-Store
                  </h3>
                  <div className={`rounded-lg p-3 space-y-2 ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-white border border-slate-200'}`}>
                    <div className="flex justify-between text-sm">
                      <span>Coffee Large</span>
                      <span className="text-emerald-500">$2.49</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Doritos</span>
                      <span className="text-emerald-500">$2.99</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Pump 3 - Premium</span>
                      <span className="text-emerald-500">$35.00</span>
                    </div>
                    <div className={`border-t pt-2 mt-2 ${theme === 'dark' ? 'border-slate-600' : 'border-slate-200'}`}>
                      <div className="flex justify-between font-bold">
                        <span>Total</span>
                        <span className="text-emerald-500">$40.48</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="text-center text-white">
                  <div className="h-20 w-20 rounded-full bg-orange-500 flex items-center justify-center mx-auto mb-4">
                    <Play className="h-10 w-10 text-white ml-1" />
                  </div>
                  <p className="text-xl font-bold">Launch Interactive Demo</p>
                  <p className="text-slate-300 text-sm">No signup required</p>
                </div>
              </div>
            </div>

            {/* Feature callouts */}
            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className={`rounded-xl p-4 text-center ${theme === 'dark' ? 'bg-slate-800/50 border border-slate-700' : 'bg-white border border-slate-200 shadow-sm'}`}>
                <MousePointer className="h-6 w-6 text-orange-500 mx-auto mb-2" />
                <p className="text-sm font-medium">Click Pumps to Prepay</p>
              </div>
              <div className={`rounded-xl p-4 text-center ${theme === 'dark' ? 'bg-slate-800/50 border border-slate-700' : 'bg-white border border-slate-200 shadow-sm'}`}>
                <Zap className="h-6 w-6 text-orange-500 mx-auto mb-2" />
                <p className="text-sm font-medium">Auto Demo Simulates Activity</p>
              </div>
              <div className={`rounded-xl p-4 text-center ${theme === 'dark' ? 'bg-slate-800/50 border border-slate-700' : 'bg-white border border-slate-200 shadow-sm'}`}>
                <Store className="h-6 w-6 text-orange-500 mx-auto mb-2" />
                <p className="text-sm font-medium">Add C-Store Items</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className={`py-12 border-y ${theme === 'dark' ? 'bg-slate-900/50 border-slate-800' : 'bg-orange-50 border-orange-100'}`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "500+", label: "Gas Stations" },
              { value: "2M+", label: "Transactions/Month" },
              { value: "99.9%", label: "Uptime SLA" },
              { value: "$2.4M", label: "Owner Savings" },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl font-bold bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">
                  {stat.value}
                </p>
                <p className={theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pain Points - Old vs New */}
      <section id="comparison" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              <span className="text-red-500">Old Way</span> vs <span className="text-emerald-500">FlowFeed Way</span>
            </h2>
            <p className={`text-lg ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>Real problems we solve every day for station owners</p>
          </div>

          <div className="space-y-4">
            {painPoints.map((point, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                className="grid md:grid-cols-2 gap-4"
              >
                {/* Old Way */}
                <div className={`rounded-xl p-5 flex items-start gap-4 ${theme === 'dark' ? 'bg-red-500/5 border border-red-500/20' : 'bg-red-50 border border-red-200'}`}>
                  <div className="h-10 w-10 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                    <X className="h-5 w-5 text-red-500" />
                  </div>
                  <div>
                    <p className="text-sm text-red-500 font-medium mb-1">Old Systems</p>
                    <p className={theme === 'dark' ? 'text-slate-300' : 'text-slate-700'}>{point.old}</p>
                  </div>
                </div>
                
                {/* New Way */}
                <div className={`rounded-xl p-5 flex items-start gap-4 ${theme === 'dark' ? 'bg-emerald-500/5 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-200'}`}>
                  <div className="h-10 w-10 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <Check className="h-5 w-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-sm text-emerald-500 font-medium mb-1">FlowFeed</p>
                    <p className={theme === 'dark' ? 'text-slate-300' : 'text-slate-700'}>{point.new}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className={`py-20 px-6 ${theme === 'dark' ? 'bg-slate-900/30' : 'bg-slate-50'}`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Everything You Need in One System</h2>
            <p className={`text-lg max-w-2xl mx-auto ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
              Stop juggling separate systems. FlowFeed handles pumps, store, and back office together.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`rounded-2xl p-6 hover:border-orange-500/30 transition-colors ${theme === 'dark' ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}
              >
                <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4`}>
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className={theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Full Comparison Table */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Feature Comparison</h2>
            <p className={`text-lg ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>FlowFeed vs Traditional Gas Station Software</p>
          </div>

          <div className={`rounded-2xl overflow-hidden ${theme === 'dark' ? 'bg-slate-900 border border-slate-800' : 'bg-white border border-slate-200 shadow-lg'}`}>
            <div className={`grid grid-cols-3 p-4 ${theme === 'dark' ? 'bg-slate-800/50' : 'bg-slate-100'}`}>
              <div className="font-bold">Feature</div>
              <div className="font-bold text-center">
                <span className="bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">FlowFeed</span>
              </div>
              <div className={`font-bold text-center ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Traditional</div>
            </div>
            {comparison.map((row, i) => (
              <div key={i} className={`grid grid-cols-3 p-4 border-t ${theme === 'dark' ? 'border-slate-800' : 'border-slate-200'} ${i % 2 === 0 ? (theme === 'dark' ? 'bg-slate-900/50' : 'bg-slate-50') : ''}`}>
                <div className={theme === 'dark' ? 'text-slate-300' : 'text-slate-700'}>{row.feature}</div>
                <div className="text-center">
                  {typeof row.flowfeed === 'boolean' ? (
                    row.flowfeed ? (
                      <Check className="h-5 w-5 text-emerald-500 mx-auto" />
                    ) : (
                      <X className="h-5 w-5 text-red-500 mx-auto" />
                    )
                  ) : (
                    <span className="text-emerald-500 font-bold">{row.flowfeed}</span>
                  )}
                </div>
                <div className="text-center">
                  {typeof row.traditional === 'boolean' ? (
                    row.traditional ? (
                      <Check className="h-5 w-5 text-emerald-500 mx-auto" />
                    ) : (
                      <X className="h-5 w-5 text-red-500 mx-auto" />
                    )
                  ) : (
                    <span className="text-red-500">{row.traditional}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Calculator */}
      <section className={`py-20 px-6 ${theme === 'dark' ? 'bg-gradient-to-b from-slate-900/30 to-slate-950' : 'bg-gradient-to-b from-orange-50 to-white'}`}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Calculate Your Savings</h2>
            <p className={`text-lg ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>See how much FlowFeed can save your station</p>
          </div>

          <div className={`rounded-2xl p-8 ${theme === 'dark' ? 'bg-slate-900 border border-slate-800' : 'bg-white border border-slate-200 shadow-lg'}`}>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Inputs */}
              <div className="space-y-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>Number of Pumps</label>
                  <input
                    type="range"
                    min="4"
                    max="24"
                    value={roiInputs.pumps}
                    onChange={(e) => setRoiInputs(prev => ({ ...prev, pumps: parseInt(e.target.value) }))}
                    className="w-full accent-orange-500"
                  />
                  <div className={`flex justify-between text-sm mt-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                    <span>4</span>
                    <span className="text-orange-500 font-bold">{roiInputs.pumps} pumps</span>
                    <span>24</span>
                  </div>
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>Daily Gallons Sold</label>
                  <input
                    type="range"
                    min="1000"
                    max="10000"
                    step="500"
                    value={roiInputs.dailyGallons}
                    onChange={(e) => setRoiInputs(prev => ({ ...prev, dailyGallons: parseInt(e.target.value) }))}
                    className="w-full accent-orange-500"
                  />
                  <div className={`flex justify-between text-sm mt-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                    <span>1,000</span>
                    <span className="text-orange-500 font-bold">{roiInputs.dailyGallons.toLocaleString()} gal/day</span>
                    <span>10,000</span>
                  </div>
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>Current Software Cost</label>
                  <input
                    type="range"
                    min="200"
                    max="800"
                    step="50"
                    value={roiInputs.currentSoftwareCost}
                    onChange={(e) => setRoiInputs(prev => ({ ...prev, currentSoftwareCost: parseInt(e.target.value) }))}
                    className="w-full accent-orange-500"
                  />
                  <div className={`flex justify-between text-sm mt-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                    <span>$200</span>
                    <span className="text-orange-500 font-bold">${roiInputs.currentSoftwareCost}/mo</span>
                    <span>$800</span>
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="bg-gradient-to-br from-orange-500/20 to-amber-500/20 border border-orange-500/30 rounded-xl p-6">
                <h3 className="text-lg font-bold mb-6 text-center">Your Estimated Savings</h3>
                
                <div className="space-y-4">
                  <div className={`rounded-lg p-4 text-center ${theme === 'dark' ? 'bg-slate-900/50' : 'bg-white/80'}`}>
                    <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>Monthly Savings</p>
                    <p className="text-4xl font-black text-emerald-500">${Math.round(roi.monthly).toLocaleString()}</p>
                  </div>
                  
                  <div className={`rounded-lg p-4 text-center ${theme === 'dark' ? 'bg-slate-900/50' : 'bg-white/80'}`}>
                    <p className="text-sm text-slate-400 mb-1">Annual Savings</p>
                    <p className="text-4xl font-black bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">
                      ${Math.round(roi.annual).toLocaleString()}
                    </p>
                  </div>
                </div>

                <p className={`text-xs text-center mt-4 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                  *Based on 2¢/gallon efficiency gains + software cost difference
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Real Results from Real Owners</h2>
            <p className={`text-lg ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>Join 500+ gas stations already using FlowFeed</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`rounded-2xl p-6 ${theme === 'dark' ? 'bg-slate-900 border border-slate-800' : 'bg-white border border-slate-200 shadow-md'}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className={`mb-4 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'}`}>"{t.quote}"</p>
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2 mb-4">
                  <p className="text-emerald-500 font-bold text-sm">{t.savings}</p>
                </div>
                <div>
                  <p className="font-bold">{t.author}</p>
                  <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>{t.role} • {t.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className={`py-20 px-6 ${theme === 'dark' ? 'bg-slate-900/30' : 'bg-slate-50'}`}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Simple, Transparent Pricing</h2>
            <p className={`text-lg ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>No hidden fees. No surprises. Cancel anytime.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {pricingPlans.map((plan, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`relative rounded-2xl p-6 ${
                  plan.popular 
                    ? 'border-2 border-orange-500 ring-2 ring-orange-500/20' 
                    : theme === 'dark' ? 'bg-slate-900 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'
                } ${plan.popular && (theme === 'dark' ? 'bg-slate-900' : 'bg-white shadow-lg')}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    MOST POPULAR
                  </div>
                )}
                <h3 className="text-xl font-bold mb-1">{plan.name}</h3>
                <p className={`text-sm mb-4 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>{plan.description}</p>
                <div className="mb-6">
                  <span className="text-4xl font-black">${plan.price}</span>
                  <span className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}>/month</span>
                </div>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                  {plan.notIncluded.map((f, j) => (
                    <li key={j} className={`flex items-center gap-2 text-sm ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                      <X className="h-4 w-4 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/signup">
                  <Button 
                    className={`w-full ${plan.popular ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white' : ''}`}
                    variant={plan.popular ? 'default' : 'outline'}
                  >
                    Start Free Trial
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className={`rounded-3xl p-12 ${theme === 'dark' ? 'bg-gradient-to-r from-orange-500/20 to-amber-500/20 border border-orange-500/30' : 'bg-gradient-to-r from-orange-100 to-amber-100 border border-orange-200'}`}>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to Modernize Your Station?</h2>
            <p className={`text-lg mb-8 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
              Join 500+ gas station owners who made the switch. Start your free trial today.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-orange-500 to-amber-500 text-white text-lg px-8"
                onClick={() => navigate('/pos/gas-station-demo')}
              >
                <Play className="h-5 w-5 mr-2" /> Try Live Demo
              </Button>
              <Link to="/signup">
                <Button size="lg" variant="outline" className="border-orange-500 text-orange-500 text-lg px-8">
                  Start Free Trial
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`py-12 px-6 border-t ${theme === 'dark' ? 'border-slate-800' : 'border-slate-200'}`}>
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center gap-6">
          <FlowFeedLogo size="small" showTagline={true} />
          <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
            © 2025 FlowFeed. All rights reserved.
          </p>
          <div className={`flex gap-6 text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
            <Link to="/privacy" className={`hover:${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Privacy</Link>
            <Link to="/terms" className={`hover:${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Terms</Link>
            <Link to="/" className={`hover:${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Main Site</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GasStationLanding;
